import { PortalRegistrationStatuses } from '@/context/Contact/useUserRegistrationStatus';
import { useLog } from '@/features/shared/Hooks/useLog';
import { authorize } from '@/services';
import { StatusCodes } from '@/utils/constants/http-codes';
import { isGlobalSearchPath, isProtectedPath } from '@/utils/portal';
import decode from 'jwt-decode';
import { isNull } from 'lodash/fp';
import React from 'react';
// import { useCookies } from 'react-cookie';
import { useStorageState } from 'react-storage-hooks';
import { useOidc, useOidcEvents } from '../Oidc';
import { usePowerApps } from '../PowerApps';
import secureLocalStorage from 'react-secure-storage';

interface AuthorizationError {
  status: number;
  data: any;
}

const isTokenExpired = (token: string) => {
  const jwt = decode(token) as any;
  return jwt.exp < Date.now() / 1000;
};

enum AuthorizationErrors {
  CRM_ABSENT = 1,
  CRM_MISSING_SUB = 2,
}

// const COOKIE_AUTH_TOKEN_NAME = 'authToken';
// const COOKIE_OPTIONS = {
//   secure: true,
//   sameSite: true,
//   // 1 day expiration date.
//   expires: new Date(Date.now() + 86400000),
// };

export const useAuthManager = () => {
  const Log = useLog({ context: 'useAuthManager' });
  const [error, setError] = React.useState<string | null>(null);
  const { signIn, isAuthenticated, manager } = useOidc();
  const { powerAppsConnect } = usePowerApps();
  const isFirstTimeAuthorizing = React.useRef(true);
  const authorizationsAttempts = React.useRef(0);
  const [isAuthorizing, setIsAuthorizing] = React.useState(isAuthenticated);
  const [storedAuthToken, setStoredAuthToken] = useStorageState<string>(
    secureLocalStorage as any,
    'authToken',
  );
  // const [{ [COOKIE_AUTH_TOKEN_NAME]: storedAuthToken = null }, setCookie, removeCookie] =
  //   useCookies([COOKIE_AUTH_TOKEN_NAME]);
  const [storedPortalRegistrationStatus, setStoredPortalRegistrationStatus] =
    useStorageState<string>(localStorage, 'portalRegistrationStatus');
  const authToken = React.useMemo(
    () => (isAuthenticated ? storedAuthToken : null),
    [isAuthenticated, storedAuthToken],
  );
  const portalRegistrationStatus = React.useMemo(
    () => (isAuthenticated ? storedPortalRegistrationStatus : null),
    [isAuthenticated, storedPortalRegistrationStatus],
  );

  const [isAuthorized, setIsAuthorized] = React.useState<boolean | null>(
    isNull(authToken) ? null : !isTokenExpired(authToken),
  );

  const [isPendingApproval, setIsPendingApproval] = React.useState<boolean | null>(
    isNull(portalRegistrationStatus) ? null : portalRegistrationStatus === '2',
  );

  const [isForbidden, setIsForbidden] = React.useState(false);

  const resetAuthorization = React.useCallback(() => {
    setStoredAuthToken(null);
    // removeCookie(COOKIE_AUTH_TOKEN_NAME, COOKIE_OPTIONS);

    setStoredPortalRegistrationStatus(null);
    setIsAuthorized(null);
    setIsForbidden(false);
  }, [setIsAuthorized, setStoredAuthToken, setStoredPortalRegistrationStatus]);
  // }, [setIsAuthorized, removeCookie, setStoredPortalRegistrationStatus]);

  /**
   * Handles OIDC authentication events
   * Sets up event handlers for user session state changes and token lifecycle events
   */
  useOidcEvents(manager, {
    /**
     * Called when user is successfully loaded/authenticated
     */
    onUserLoaded: () => {
      Log(':: User successfully loaded');
    },

    /**
     * Called when user is unloaded/logged out
     */
    onUserUnloaded: () => {
      Log(':: User unloaded');
    },

    /**
     * Called when the access token has expired
     * Redirects to sign in if on a protected path
     */
    onAccessTokenExpired: () => {
      Log(':: Access token expired');
      if (isProtectedPath()) {
        signIn();
      }
    },
    /**
     * Called when the access token is about to expire
     * Proactively redirects to sign in if on the global search path
     * Logs that the token is expiring
     */
    onAccessTokenExpiring: () => {
      if (isGlobalSearchPath(window.location.pathname)) {
        signIn();
      }
      Log(':: Access token expiring');
    },
  });

  /**
   * Request Authorization
   */
  const requestAuthorization = React.useCallback(
    async (reset = false) => {
      if (authToken && !reset) {
        if (!isTokenExpired(authToken)) {
          setIsAuthorized(true);
          setIsForbidden(false);
          setIsAuthorizing(false);
          setError('Unnecessary authorization request');
          return;
        }
      }

      setIsAuthorized(null);
      setIsForbidden(false);
      setIsAuthorizing(true);
      setStoredAuthToken(null);
      // removeCookie(COOKIE_AUTH_TOKEN_NAME, COOKIE_OPTIONS);

      /**
       * Handles forbidden errors that occur during the authorization process.
       * @async
       * @param {AuthorizationError} param - The error object containing the error data.
       * @returns {Promise<void>} - A Promise that resolves when the function completes.
       */
      const handleForbiddenError = async ({ data }: AuthorizationError): Promise<void> => {
        const {
          authToken: errorAuthToken,
          portalRegistrationStatus: errorPortalRegistrationStatus,
        } = data || {};

        if (errorAuthToken) {
          setStoredAuthToken(errorAuthToken);
          // setCookie(COOKIE_AUTH_TOKEN_NAME, errorAuthToken, COOKIE_OPTIONS);
        }

        if (errorPortalRegistrationStatus) {
          Log(`Forbidden error: Portal registration status ${errorPortalRegistrationStatus}`);
          setStoredPortalRegistrationStatus(errorPortalRegistrationStatus.toString());
          setIsPendingApproval(
            errorPortalRegistrationStatus?.toString() === PortalRegistrationStatuses.REGISTERED
              ? true
              : null,
          );
        }

        setIsForbidden(true);
      };

      /**
       * Handles unauthorized errors that occur during the authorization process.
       * @async
       * @param {AuthorizationError} param - The error object containing the error code.
       * @returns {Promise<void>} - A Promise that resolves when the function completes.
       */
      const handleUnauthorizedError = async ({ data }: AuthorizationError): Promise<void> => {
        const isMissingContactError = data?.errorCode === AuthorizationErrors.CRM_ABSENT;
        const isMissingSubError = data?.errorCode === AuthorizationErrors.CRM_MISSING_SUB;
        const isFirstAuthorizationAttempt = !authorizationsAttempts.current;

        if ((isMissingContactError || isMissingSubError) && isFirstAuthorizationAttempt) {
          const authError = isMissingContactError ? '[Missing Contact]' : '[Missing Subclaim]';
          Log(`Unauthorized error ${authError}, establishing connection with PowerApps`);
          authorizationsAttempts.current = 1;
          await powerAppsConnect({ force: true });
          // eslint-disable-next-line
          await getAuthorization();
        }
      };

      const getAuthorization = async () => {
        try {
          Log('Getting authorization');
          const nextAuthToken = await authorize();

          setStoredAuthToken(nextAuthToken);
          // setCookie(COOKIE_AUTH_TOKEN_NAME, nextAuthToken, COOKIE_OPTIONS);

          // Adding Timeout due to Safari localStorage delay issue
          setTimeout(() => {
            setIsAuthorizing(false);
            setIsAuthorized(true);
          });
        } catch (err) {
          Log('An error occurred getting the authorization');
          const errorResponse = (err as any).response || { status: null, data: null };

          const { status } = errorResponse;

          if (status === StatusCodes.FORBIDDEN) {
            await handleForbiddenError(errorResponse);
          }

          if (status === StatusCodes.UNAUTHORIZED) {
            await handleUnauthorizedError(errorResponse);
          }

          setIsAuthorizing(false);
          setIsAuthorized(false);
          setError((err as any).message);
        }
      };

      await getAuthorization();
    },
    [setStoredAuthToken, setStoredPortalRegistrationStatus, authToken, Log, powerAppsConnect],
    // [authToken, removeCookie, setCookie, Log, setStoredPortalRegistrationStatus, powerAppsConnect],
  );

  React.useEffect(() => {
    if (!isFirstTimeAuthorizing.current) return;

    if (isAuthenticated) {
      isFirstTimeAuthorizing.current = false;
      requestAuthorization();
    }
  }, [requestAuthorization, isAuthenticated]);

  return {
    isAuthorizing,
    isAuthorized,
    isPendingApproval,
    isForbidden,
    authToken,
    requestAuthorization,
    resetAuthorization,
    error,
  };
};
